import { ChevronDown, Moon, Palette, Sun } from 'lucide-react'
import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { setThemePreferences } from '@/lib/theme/theme-store'
import { useTheme } from '@/lib/theme/use-theme'
import { ThemePalettePicker } from './ThemePalettePicker'

export function ThemeMenu() {
  const theme = useTheme()
  const id = useId()
  const trigger = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, maxHeight: 440 })
  const dark = theme.resolvedTheme === 'dark'

  useLayoutEffect(() => {
    if (!open) return
    function reposition() {
      const anchor = trigger.current?.getBoundingClientRect()
      if (!anchor) return
      const width = Math.min(300, document.documentElement.clientWidth - 24)
      const top = anchor.bottom + 6
      setPosition({
        top,
        left: Math.max(
          12,
          Math.min(
            anchor.right - width,
            document.documentElement.clientWidth - width - 12,
          ),
        ),
        maxHeight: Math.min(440, window.innerHeight - top - 12),
      })
    }
    function dismissOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      // Safari does not focus buttons on pointer activation; restore it explicitly.
      event.preventDefault()
      document.getElementById(id)?.hidePopover()
      trigger.current?.focus({ preventScroll: true })
    }
    reposition()
    window.addEventListener('keydown', dismissOnEscape)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('keydown', dismissOnEscape)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, id])

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <button
        ref={trigger}
        type="button"
        popoverTarget={id}
        aria-label="Appearance"
        title="Accent and palette"
        aria-expanded={open}
        aria-controls={id}
        aria-haspopup="dialog"
        className="flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-surface px-2 cursor-pointer text-muted transition-colors hover:bg-hover hover:text-primary"
      >
        <Palette size={15} aria-hidden="true" />
        <ChevronDown
          size={12}
          aria-hidden="true"
          className={open ? 'rotate-180' : ''}
        />
      </button>
      <button
        type="button"
        aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        className="grid size-8 shrink-0 place-items-center rounded-lg cursor-pointer bg-surface text-muted transition-colors hover:bg-hover hover:text-primary"
        onClick={(event) =>
          setThemePreferences(
            { mode: dark ? 'light' : 'dark' },
            { transitionOrigin: event.currentTarget },
          )
        }
      >
        {dark ? (
          <Moon size={16} aria-hidden="true" />
        ) : (
          <Sun size={16} aria-hidden="true" />
        )}
      </button>
      {createPortal(
        <div
          id={id}
          popover="auto"
          role="dialog"
          aria-label="Appearance"
          onToggle={(event) => setOpen(event.newState === 'open')}
          className="theme-palette-popover fixed m-0 w-[min(300px,calc(100vw-24px))] overflow-y-auto overscroll-contain rounded-[10px] border border-border-strong bg-raised p-0 text-primary shadow-lg"
          style={position}
        >
          <ThemePalettePicker />
        </div>,
        document.body,
      )}
    </div>
  )
}
